"use client";

import { Link, usePathname } from "@/i18n/navigation";
import { InstagramIcon, LinkedInIcon, TikTokIcon } from "@/components/icons/BrandIcons";
import { useTranslations, useLocale } from "next-intl";
import { NewsletterForm } from "@/components/shared/ui/NewsletterForm";

export default function Footer({ is_signed_in = false }: { is_signed_in?: boolean }) {
	const pathname = usePathname();

	const t = useTranslations();
	const locale = useLocale();
	const logo_href = is_signed_in ? "/app" : "/";
	const primary_href = is_signed_in ? "/app" : "/login";
	const primary_label = is_signed_in ? "Open app" : "Login";

	if (pathname.includes('/book-a-demo')) return <></>;

	return (
		<footer className="flex w-full flex-col justify-around bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue relative overflow-hidden">
			{/* Main Footer Content */}
			<div className="py-10 sm:py-24 px-8 sm:px-12 lg:px-20 text-white relative z-10">
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8">
					{/* Left Side: Logo, Button, and Social Media */}
					<div className="col-span-1 flex flex-col items-center lg:items-start lg:col-span-4">
						{/* Mobile Layout */}
						<div className="flex flex-col items-center sm:hidden w-full">
							{/* Logo and social media icons side-by-side */}
							<div className="flex items-center justify-center space-x-4 mb-6">
								{/* Logo */}
								<Link href={logo_href}>
										<img
											src="/StudyStash_Logo_White.png"
											alt="StudyStash Logo"
											width={120}
											className="h-auto cursor-pointer object-contain"
											loading="lazy"
											decoding="async"
										/>
								</Link>

								{/* Social media icons */}
								<div className="flex space-x-2">
									<Link
										href="https://www.linkedin.com/company/studystash/"
										className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
										aria-label="Follow StudyStash on LinkedIn"
									>
										<LinkedInIcon size={24} />
									</Link>
									<Link
										href="https://www.instagram.com/studystash_app/"
										className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
										aria-label="Follow StudyStash on Instagram"
									>
										<InstagramIcon size={24} />
									</Link>
									<Link
										href="https://www.tiktok.com/@studystash?_t=8eJSsXD5NqU&_r=1"
										className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
										aria-label="Follow StudyStash on TikTok"
									>
										<TikTokIcon size={24} />
									</Link>
								</div>
							</div>

							{/* Newsletter form */}
							<div className="w-full pb-4 flex justify-center max-w-sm">
								<NewsletterForm />
							</div>
						</div>

						{/* Tablet+ Layout */}
						<div className="hidden sm:flex sm:flex-col sm:items-start w-full">
							<div className="flex justify-between items-start w-full lg:flex-col">
								<div className="flex flex-col">
									<Link href={logo_href} className="mb-0 sm:mb-4">
											<img
												src="/StudyStash_Logo_White.png"
												alt="StudyStash Logo"
												width={208}
												className="h-auto cursor-pointer object-contain"
												loading="lazy"
												decoding="async"
											/>
									</Link>

									<div className="flex space-x-2">
										<Link
											href="https://www.linkedin.com/company/studystash/"
											className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
											aria-label="Follow StudyStash on LinkedIn"
										>
											<LinkedInIcon className="w-6 h-6 lg:w-7 lg:h-7" />
										</Link>
										<Link
											href="https://www.instagram.com/studystash_app/"
											className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
											aria-label="Follow StudyStash on Instagram"
										>
											<InstagramIcon className="w-6 h-6 lg:w-7 lg:h-7" />
										</Link>
										<Link
											href="https://www.tiktok.com/@studystash?_t=8eJSsXD5NqU&_r=1"
											className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:bg-white/20 hover:scale-105"
											aria-label="Follow StudyStash on TikTok"
										>
											<TikTokIcon className="w-6 h-6 lg:w-7 lg:h-7" />
										</Link>
									</div>
								</div>

								<div className="pl-8 lg:pl-0 pt-0 lg:pt-8 w-full">
									<NewsletterForm />
								</div>
							</div>
						</div>
					</div>

					{/* Right Side: 4 Column Grid */}
					<div className="col-span-1 grid grid-cols-2 gap-y-4 gap-x-8 sm:grid-cols-4 sm:gap-4 sm:pt-8 lg:pt-0 lg:col-span-8 lg:gap-2">
						{/* Column 1: Who we help */}
						<div className="flex flex-col">
							<h5 className="text-white text-lg font-semibold pb-2 sm:mb-2">Who we help</h5>
							<div className="flex flex-col sm:hidden">
								<Link
									href="/who-we-help/universities"
									className="text-white! font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Universities
								</Link>
							<Link
								href={locale === 'en-us' ? "/who-we-help/community-colleges" : "/who-we-help/fe-colleges"}
								className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
							>
								{t("navbar.fe-colleges")}
							</Link>
								{/* Training Providers - Temporarily Hidden */}
								{/* <button
									onClick={() => toast(() => (
										<span>
											Book a Demo <Link href="/book-a-demo" className="text-blue-500 hover:underline">here</Link>
										</span>
									))}
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base text-left"
								>
									Training Providers
								</button> */}
								<Link
									href="/who-we-help/students"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Students
								</Link>
							<Link
								href="/who-we-help/lecturers"
								className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
							>
								{t("general.words.lecturers")}
							</Link>
							</div>
							<ul className="hidden list-none sm:flex sm:flex-col">
								<li className="list-none">
									<Link href="/who-we-help/universities" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Universities
									</Link>
								</li>
							<li className="list-none">
								<Link href={locale === 'en-us' ? "/who-we-help/community-colleges" : "/who-we-help/fe-colleges"} className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
									{t("navbar.fe-colleges")}
								</Link>
							</li>
								{/* Training Providers - Temporarily Hidden */}
								{/* <li className="list-none">
									<button
										onClick={() => toast(() => (
											<span>
												Book a Demo <Link href="/book-a-demo" className="text-blue-500 hover:underline">here</Link>
											</span>
										))}
										className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base text-left"
									>
										Training Providers
									</button>
								</li> */}
								<li className="list-none">
									<Link href="/who-we-help/students" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Students
									</Link>
								</li>
							<li className="list-none">
								<Link href="/who-we-help/lecturers" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
									{t("general.words.lecturers")}
								</Link>
							</li>
							</ul>
						</div>

						{/* Column 2: How we help */}
						<div className="flex flex-col">
							<h5 className="text-white! text-lg font-semibold pb-2 sm:mb-2">How we help</h5>
							<div className="flex flex-col space-y-0.5 sm:hidden">
								<Link
									href="/how-we-help/ai-with-guardrails"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									AI with Guardrails
								</Link>
								<Link
									href="/how-we-help/neuroscience"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Neuroscience
								</Link>
								<Link
									href="/how-we-help/adaptive-learning"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Adaptive Learning
								</Link>
								<Link
									href="/how-we-help/reduced-workload"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Reduced Workload
								</Link>
								<Link
									href="/how-we-help/learner-insights"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Learner Insights
								</Link>
							</div>
							<ul className="hidden list-none sm:flex sm:flex-col">
								<li className="list-none">
									<Link href="/how-we-help/ai-with-guardrails" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										AI with Guardrails
									</Link>
								</li>
								<li className="list-none">
									<Link href="/how-we-help/neuroscience" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Neuroscience
									</Link>
								</li>
								<li className="list-none">
									<Link href="/how-we-help/adaptive-learning" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Adaptive Learning
									</Link>
								</li>
								<li className="list-none">
									<Link href="/how-we-help/reduced-workload" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Reduced Workload
									</Link>
								</li>
								<li className="list-none">
									<Link href="/how-we-help/learner-insights" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Learner Insights
									</Link>
								</li>
							</ul>
						</div>

						{/* Column 3: About */}
						<div className="flex flex-col">
							<h5 className="text-white! text-lg font-semibold pb-2 sm:mb-2">About</h5>
							<div className="flex flex-col space-y-0.5 sm:hidden">
								<Link
									href="/about/our-story"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Our Story
								</Link>
								<Link
									href="/about/events-webinars"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Events and Webinars
								</Link>
								<Link
									href="https://trust.studystash.com/"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
									target="_blank"
									rel="noopener noreferrer"
								>
									Trust and Safety Hub
								</Link>
								<Link
									href="https://internal.support.studystash.com/servicedesk/customer/portals"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									{t("navbar.help-centre")}
								</Link>
							</div>
							<ul className="hidden list-none sm:flex sm:flex-col">
								<li className="list-none">
									<Link href="/about/our-story" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Our Story
									</Link>
								</li>
								<li className="list-none">
									<Link href="/about/events-webinars" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Events and Webinars
									</Link>
								</li>
								<li className="list-none">
									<Link href="https://trust.studystash.com/" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base" target="_blank" rel="noopener noreferrer">
										Trust and Safety Hub
									</Link>
								</li>
								<li className="list-none">
									<Link href="https://internal.support.studystash.com/servicedesk/customer/portals" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										{t("navbar.help-centre")}
									</Link>
								</li>
							</ul>
						</div>

						{/* Column 4: Find out more */}
						<div className="flex flex-col">
							<h5 className="text-white! text-lg font-semibold pb-2 sm:mb-2">Find out more</h5>
							<div className="flex flex-col space-y-0.5 sm:hidden">
								<Link
									href="/book-a-demo"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Book a Demo
								</Link>
								{!is_signed_in ? (
									<Link
										href="/register"
										className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
									>
										Sign up
									</Link>
								) : null}
								<Link
									href={primary_href}
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									{primary_label}
								</Link>
								<Link
									href="/contact-us"
									className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base"
								>
									Contact Us
								</Link>
							</div>
							<ul className="hidden list-none sm:flex sm:flex-col">
								<li className="list-none">
									<Link href="/book-a-demo" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Book a Demo
									</Link>
								</li>
								{!is_signed_in ? (
									<li className="list-none">
										<Link href="/register" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
											Sign up
										</Link>
									</li>
								) : null}
								<li className="list-none">
									<Link href={primary_href} className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										{primary_label}
									</Link>
								</li>
								<li className="list-none">
									<Link href="/contact-us" className="text-white font-normal transition duration-150 ease-in-out hover:text-white/80 text-base">
										Contact Us
									</Link>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
			<div className="w-full px-8 sm:px-12 lg:px-20 flex flex-col items-center justify-between border-t border-white/20 pb-12 pt-8 text-white bg-white lg:flex-row sm:py-12 relative z-10">
				<div className="flex flex-col items-center lg:items-start gap-4 lg:gap-2 text-gray-800 w-full lg:w-auto">
					<div className="flex flex-wrap gap-4 text-sm justify-center lg:justify-start">
						<Link href="/privacy-policy" className="text-sm lg:text-xs underline hover:text-gray-600 transition-colors">
							Privacy Policy
						</Link>
						<Link href="/terms-of-use" className="text-sm lg:text-xs underline hover:text-gray-600 transition-colors">
							Terms of Use
						</Link>
						<Link href="/accessibility-statement" className="text-sm lg:text-xs underline hover:text-gray-600 transition-colors">
							Accessibility Statement
						</Link>
						<Link href="/trustworthy-and-responsible-ai" className="text-sm lg:text-xs underline hover:text-gray-600 transition-colors">
							Trustworthy and Responsible AI
						</Link>
					</div>
					<div className="text-sm lg:text-xs text-center lg:text-left space-y-1">
						<span>&copy; Copyright {t("general.entity")} 2026, All rights reserved.</span>
						<div>{t("general.address")}</div>
						{/* <div>Company Number: 14877078</div> */}
					</div>
				</div>

				{/* Partner and Certification Logos */}
				<div className="flex items-center gap-6 mt-8 lg:mt-0 flex-wrap justify-center lg:justify-end w-full lg:w-auto lg:py-2">
						<img
							src="/EAB.png"
							alt="EAB Partner"
							className="h-8 lg:h-8 xl:h-10 w-auto"
							loading="lazy"
							decoding="async"
							fetchPriority="low"
						/>
						<img
							src="/educause.png"
							alt="EDUCAUSE Member"
							className="h-8 lg:h-8 xl:h-10 w-auto"
							loading="lazy"
							decoding="async"
							fetchPriority="low"
						/>
					{locale !== 'en-us' && (
							<img
								src="/ALT-logo.png"
								alt="Association for Learning Technology"
								className="h-12 lg:h-12 xl:h-16 w-auto"
								loading="lazy"
								decoding="async"
								fetchPriority="low"
							/>
					)}
					{locale === 'en-us' ? (
						<>
								<img
									src="/COPPA.png"
									alt="COPPA Compliant"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
								<img
									src="/FERPA.png"
									alt="FERPA Compliant"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
								<img
									src="/CCPA.png"
									alt="CCPA Compliant"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
								<img
									src="/ISO_IEC 27001_2022.png"
									alt="ISO 27001 Certified"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
								<img
									src="/508.png"
									alt="Section 508 Compliant"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
						</>
					) : (
						<>
								<img
									src="/ISO_IEC 27001_2022.png"
									alt="ISO 27001 Certified"
									className="h-12 lg:h-10 xl:h-14 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
								<img
									src="/gdpr_badge.png"
									alt="GDPR Compliant"
									className="h-12 lg:h-12 xl:h-16 w-auto"
									loading="lazy"
									decoding="async"
									fetchPriority="low"
								/>
						</>
					)}
				</div>
			</div>
		</footer>
	);
}
